import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { blogArticles } from '../lib/content';

const pageText = {
  de: {
    subtitle: 'Wissen & Insights',
    title: 'Blog',
    readMore: 'Weiterlesen'
  },
  it: {
    subtitle: 'Conoscenza & Approfondimenti',
    title: 'Blog',
    readMore: 'Leggi di più'
  },
  en: {
    subtitle: 'Knowledge & Insights',
    title: 'Blog',
    readMore: 'Read more'
  },
  es: {
    subtitle: 'Conocimiento & Perspectivas',
    title: 'Blog',
    readMore: 'Leer más'
  }
};

export default function BlogPage() {
  const { language } = useLanguage();
  const location = useLocation();
  const text = pageText[language] || pageText.de;

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <main className="min-h-screen bg-white pt-24" data-testid="blog-page">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16 md:mb-24"
        >
          <span className="text-sm text-[#999] uppercase tracking-wider mb-4 block">
            {text.subtitle}
          </span>
          <h1 className="text-huge text-[#0a0a0a] font-['Manrope']">
            {text.title}
          </h1>
        </motion.div>

        {/* Blog List */}
        <div className="border-t border-[#e5e5e5]">
          {blogArticles.map((article, index) => (
            <motion.article
              key={article.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="blog-card group"
              data-testid={`blog-article-${article.id}`}
            >
              <Link to={`/blog/${article.slug}`} className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
                {/* Image */}
                <div className="aspect-[16/10] overflow-hidden bg-[#f8f8f8]">
                  <img
                    src={article.image}
                    alt={article.title[language] || article.title.de}
                    className="w-full h-full object-cover img-grayscale"
                    width="400"
                    height="250"
                    loading="lazy"
                  />
                </div>

                {/* Content */}
                <div className="md:col-span-2">
                  <div className="flex items-center gap-4 mb-3 text-xs text-[#999]">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(article.publishedAt).toLocaleDateString(language === 'de' ? 'de-CH' : language === 'it' ? 'it-CH' : language === 'es' ? 'es-ES' : 'en-GB')}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {article.readTime}
                    </span>
                    <span className="px-2 py-1 bg-[#f8f8f8] text-[#666]">
                      {article.category}
                    </span>
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-[#8B5CF6] font-['Manrope'] mb-2 group-hover:text-[#7C3AED] transition-colors">
                    {article.title[language] || article.title.de}
                  </h2>
                  <p className="text-[#666] text-sm">
                    {article.excerpt[language] || article.excerpt.de}
                  </p>
                </div>

                {/* Arrow */}
                <div className="hidden md:flex justify-end">
                  <div className="w-12 h-12 border border-[#e5e5e5] rounded-full flex items-center justify-center group-hover:bg-[#0a0a0a] group-hover:border-[#0a0a0a] transition-colors">
                    <ArrowRight className="w-5 h-5 text-[#0a0a0a] group-hover:text-white transition-colors" />
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </main>
  );
}
